import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, HeartHandshake, TrendingUp, Sparkles, ArrowRight, Download } from "lucide-react";
import PositioningHero from "@/components/PositioningHero";
import MavulaVsDubai from "@/components/MavulaVsDubai";
import ImpactCalculator from "@/components/ImpactCalculator";
import StabilityChart from "@/components/StabilityChart";

const ADVANTAGES = [
  {
    i: <ShieldCheck size={22}/>,
    title: "Security you can verify",
    points: [
      { b: "Independent Title Check", d: "Every deed reviewed by local lawyers on a public verifier list, where local law permits." },
      { b: "Forever Yours promise",       d: "No reversion clauses, no state takeover, no fine print tricks." },
      { b: "Anti-speculation rules",      d: "Limits on bulk buying to keep homes accessible for families." },
    ],
  },
  {
    i: <HeartHandshake size={22}/>,
    title: "Access built for our citizens",
    points: [
      { b: "Diaspora safe harbour", d: "No risk of status checks affecting your property." },
      { b: "Student & worker welcome", d: "You don't need to be rich — just a citizen with a dream." },
      { b: "Group ownership", d: "Families, friends or village groups can buy together with clear shared titles." },
    ],
  },
  {
    i: <TrendingUp size={22}/>,
    title: "Sustainable growth we can measure",
    points: [
      { b: "AfCFTA-backed", d: "Values rise with regional trade, jobs and infrastructure — not speculation." },
      { b: "Real demand first", d: "We build where people actually want to live, not where it looks good." },
      { b: "Low overheads", d: "£0 agent commission, low admin fees — more value goes to your asset." },
    ],
  },
  {
    i: <Sparkles size={22}/>,
    title: "Impact + profit — double win",
    points: [
      { b: "Impact calculator", d: "Every investment creates local jobs, homes, and trained builders." },
      { b: "Skills link", d: "Every project funds local construction training via Pula Learn." },
      { b: "Community first", d: "No gated isolation. Mixed communities, schools, markets included." },
    ],
  },
];

export default function WhyMavulaPage() {
  useEffect(() => { document.title = "Why MAVULA · A home for our people"; }, []);
  return (
    <main data-testid="why-mavula-page">
      <PositioningHero />
      <MavulaVsDubai withDownload={false} />

      {/* Advantages */}
      <section className="py-16 md:py-24 border-t mv-hairline">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ What we do differently — for our people</span></div>
          <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
            Four commitments <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>we hold to every day.</span>
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {ADVANTAGES.map((a, i) => (
              <div key={a.title} className="mv-card p-6" data-testid={`why-advantage-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>{a.i}</div>
                  <div className="font-display text-xl mv-green-text">{a.title}</div>
                </div>
                <ul className="space-y-2.5">
                  {a.points.map((p) => (
                    <li key={p.b} className="text-[14px]">
                      <b className="mv-green-text">{p.b}.</b>{" "}
                      <span style={{ color: "var(--mv-muted)" }}>{p.d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <StabilityChart />
      <ImpactCalculator />

      {/* Final CTA */}
      <section className="py-16 md:py-24 mv-green-bg text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ One-Page Summary</span></div>
          <h2 className="font-display text-3xl md:text-5xl mt-3 leading-[1.05]">
            Take it with you. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Share it.</span>
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-white/85 text-[15px]">
            One PDF, one page, everything an investor needs before their first call with us. Send it to your accountant, your family, your board.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href="/downloads/Why-MAVULA.pdf" download className="btn-gold inline-flex items-center gap-2" data-testid="why-download-pdf">
              <Download size={16}/> Download Why-MAVULA PDF
              <span className="text-[9.5px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full ml-1" style={{ background: "rgba(255,255,255,0.18)", color: "#ffd" }}>SAMPLE</span>
            </a>
            <Link to="/register-interest" className="btn-ghost !text-white !border-white/40" data-testid="why-open-account">
              Open an account <ArrowRight size={16}/>
            </Link>
          </div>
          <p className="mt-4 text-[12px] font-accent italic text-white/70">
            The PDF is a <strong className="text-white">SAMPLE</strong> — watermarked and illustrative only. Real, legally-signed versions are shared privately after eligibility screening.
          </p>
        </div>
      </section>
    </main>
  );
}
